import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaSlidersH,
  FaFire,
  FaFilter,
  FaRobot,
  FaShieldAlt,
  FaExclamationTriangle,
  FaPlayCircle
} from 'react-icons/fa';
import HeatmapFilterBar from './components/HeatmapFilterBar';
import HeatmapSettingsTab from './components/tabs/HeatmapSettingsTab';
import HeatmapViewerTab from './components/tabs/HeatmapViewerTab';
import CustomerFunnelsTab from './components/tabs/CustomerFunnelsTab';
import JourneyMiningTab from './components/tabs/JourneyMiningTab';
import FrictionSignalsTab from './components/tabs/FrictionSignalsTab';
import SessionReplayTab from './components/tabs/SessionReplayTab';

import { useHeatmapData } from './hooks/useHeatmapData';
import { useCustomerAnalyticsData } from './hooks/useCustomerAnalyticsData';
import { recordCustomerAnalyticsViewed } from '../../services/customerAnalytics.service';
import { getDaysAgoIsoDate, getTodayIsoDate } from './shared/heatmapViewUtils';
import './EndUserAnalyticsHub.css';

const TABS = [
  { id: 'heatmaps', label: 'Heatmap Viewer', icon: <FaFire />, requiresHeatmap: true },
  { id: 'settings', label: 'Heatmap Settings', icon: <FaSlidersH />, requiresHeatmap: true },
  { id: 'mining', label: 'Journey Mining', icon: <FaRobot />, requiresCustomerAnalytics: true },
  { id: 'funnels', label: 'Funnels & Segments', icon: <FaFilter />, requiresCustomerAnalytics: true },
  { id: 'friction', label: 'Friction Signals', icon: <FaExclamationTriangle />, requiresCustomerAnalytics: true },
  { id: 'replay', label: 'Session Player', icon: <FaPlayCircle />, requiresCustomerAnalytics: true, requiresSessionReplay: true }
];

/**
 * Main Hub for Heatmap & Customer Analytics.
 * Orchestrates filter state, data hooks, and tab views.
 */
export default function EndUserAnalyticsHub({
  orgId,
  roles = [],
  activeSection = 'all',
  hasHeatmapFeature = true,
  hasCustomerAnalyticsFeature = true,
  hasSessionReplayFeature = true,
  onNavigate
}) {
  const isHeatmapSection = activeSection === 'heatmap-analytics' || activeSection === 'heatmap-settings';
  const isCustomerAnalyticsSection = activeSection === 'analytics-customer' || activeSection === 'customer-analytics' || activeSection === 'behavioural-analytics' || activeSection === 'behavioral-analytics' || activeSection === 'journey-mining' || activeSection === 'friction-signals' || activeSection === 'session-replay';

  const getDefaultTab = () => {
    if (activeSection === 'heatmap-settings') return 'settings';
    if (activeSection === 'heatmap-analytics') return 'heatmaps';
    if (activeSection === 'session-replay') return 'replay';
    if (activeSection === 'funnels' || activeSection === 'customer-funnels') return 'funnels';
    if (activeSection === 'journey-mining') return 'mining';
    if (activeSection === 'analytics-customer' || activeSection === 'customer-analytics' || activeSection === 'behavioural-analytics' || activeSection === 'behavioral-analytics') {
      return 'mining';
    }
    if (activeSection === 'friction-signals') return 'friction';
    if (hasHeatmapFeature) return 'heatmaps';
    if (hasCustomerAnalyticsFeature) return 'mining';
    return 'settings';
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab);

  // Sync outer section navigation to active tab
  useEffect(() => {
    if (activeSection === 'heatmap-settings') {
      setActiveTab('settings');
    } else if (activeSection === 'heatmap-analytics') {
      setActiveTab('heatmaps');
    } else if (activeSection === 'session-replay') {
      setActiveTab('replay');
    } else if (activeSection === 'funnels' || activeSection === 'customer-funnels') {
      setActiveTab('funnels');
    } else if (activeSection === 'journey-mining') {
      setActiveTab('mining');
    } else if (activeSection === 'analytics-customer' || activeSection === 'customer-analytics' || activeSection === 'behavioural-analytics' || activeSection === 'behavioral-analytics') {
      setActiveTab('mining');
    } else if (activeSection === 'friction-signals') {
      setActiveTab('friction');
    }
  }, [activeSection, hasHeatmapFeature, hasCustomerAnalyticsFeature]);

  // Filters state
  const [filters, setFilters] = useState({
    organizationId: orgId,
    siteCollectionId: '',
    fromDate: getDaysAgoIsoDate(7),
    toDate: getTodayIsoDate(),
    pageId: '',
    deviceClass: 'all'
  });

  const [selectedPageId, setSelectedPageId] = useState('');
  const [selectedLayoutVersion, setSelectedLayoutVersion] = useState(1);
  const [selectedReplaySessionId, setSelectedReplaySessionId] = useState('');
  const [funnelActionIds, setFunnelActionIds] = useState([]);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const handleWatchReplay = useCallback((sessionId) => {
    setSelectedReplaySessionId(sessionId);
    setActiveTab('replay');
  }, []);

  // Comparison period for funnels tab (previous 7 days)
  const comparisonPeriod = useMemo(() => ({
    leftFromDate: getDaysAgoIsoDate(14),
    leftToDate: getDaysAgoIsoDate(7)
  }), []);

  // 1. Heatmap Data Hook
  const {
    settings,
    etag,
    settingsLoading,
    settingsError,
    isConcurrencyConflict,
    saveSettings,
    refetchSettings,
    summary,
    daily,
    density,
    scrollReach,
    hoverDwell,
    attention,
    metricsLoading,
    metricsError,
    refetchMetrics
  } = useHeatmapData({
    organizationId: filters.organizationId,
    siteCollectionId: filters.siteCollectionId,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    pageId: selectedPageId,
    layoutVersion: selectedLayoutVersion || 1,
    deviceClass: filters.deviceClass
  }, {
    enabled: Boolean(filters.organizationId && filters.siteCollectionId),
    enableMetrics: activeTab === 'heatmaps'
  });

  // 2. Customer Analytics Hook
  // Only enabled when Funnels or Journey Mining tabs are active
  const isCustomerAnalyticsActive = activeTab === 'funnels' || activeTab === 'mining';

  const {
    report,
    funnel,
    comparison,
    mining,
    checkpoints,
    loading: customerLoading,
    error: customerError,
    checkpointsLoading,
    generatingFlow,
    flowGenerationResult,
    generateFlow,
    fetchCheckpointReview,
    exportInteractions,
    refetch: refetchCustomer
  } = useCustomerAnalyticsData({
    organizationId: filters.organizationId,
    siteCollectionId: filters.siteCollectionId,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    pageId: selectedPageId,
    deviceClass: filters.deviceClass,
    funnelActionIds,
    comparisonPeriod
  }, {
    enabled: Boolean(filters.organizationId && filters.siteCollectionId && isCustomerAnalyticsActive)
  });

  // Synchronize selectedPageId with loaded settings.pages
  useEffect(() => {
    const availablePages = settings?.pages || [];

    if (!availablePages || availablePages.length === 0) {
      if (selectedPageId) setSelectedPageId('');
      return;
    }

    if (activeTab === 'friction' && selectedPageId === '') {
      return;
    }

    const isCurrentPageValid = availablePages.some((p) => p.pageId === selectedPageId);
    if (!isCurrentPageValid) {
      if (activeTab === 'heatmaps') {
        setSelectedPageId(availablePages[0].pageId);
      } else {
        setSelectedPageId('');
      }
    }
  }, [settings, selectedPageId, activeTab]);

  const handlePageChange = useCallback((newPageId) => {
    setSelectedPageId(newPageId);
    const pageObj = (settings?.pages || []).find((p) => p.pageId === newPageId);
    if (pageObj && pageObj.layoutVersion) {
      setSelectedLayoutVersion(pageObj.layoutVersion);
    } else {
      setSelectedLayoutVersion(1);
    }
    if (typeof window !== 'undefined' && filters.organizationId && filters.siteCollectionId) {
      try {
        const key = `cognior_active_page_${filters.organizationId}_${filters.siteCollectionId}`;
        if (newPageId) {
          window.localStorage.setItem(key, newPageId);
        } else {
          window.localStorage.removeItem(key);
        }
      } catch (e) {}
    }
  }, [filters.organizationId, filters.siteCollectionId, settings?.pages]);

  // Record customer_analytics.viewed receipt when Customer Analytics tabs are viewed
  const isCustomerAnalyticsTab = activeTab === 'funnels' || activeTab === 'mining';
  useEffect(() => {
    if (
      isCustomerAnalyticsTab &&
      hasCustomerAnalyticsFeature &&
      filters.organizationId &&
      filters.siteCollectionId
    ) {
      const sessionKey = `ca_viewed_${filters.organizationId}_${filters.siteCollectionId}`;
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          if (window.sessionStorage.getItem(sessionKey)) {
            return;
          }
          window.sessionStorage.setItem(sessionKey, '1');
        }
      } catch (e) {
        // sessionStorage restricted/unavailable; continue
      }
      recordCustomerAnalyticsViewed(filters.organizationId, filters.siteCollectionId);
    }
  }, [
    isCustomerAnalyticsTab,
    hasCustomerAnalyticsFeature,
    filters.organizationId,
    filters.siteCollectionId
  ]);

  // Handle filter changes from Toolbar
  const handleFilterChange = useCallback((newFilters) => {
    setFilters((prev) => {
      const isSiteOrOrgChanged =
        prev.siteCollectionId !== newFilters.siteCollectionId ||
        prev.organizationId !== newFilters.organizationId;
      if (isSiteOrOrgChanged) {
        setSelectedPageId('');
      }
      return {
        ...prev,
        organizationId: newFilters.organizationId,
        siteCollectionId: newFilters.siteCollectionId,
        fromDate: newFilters.fromDate,
        toDate: newFilters.toDate
      };
    });
  }, []);

  const [replayLoading, setReplayLoading] = useState(false);
  const [frictionLoading, setFrictionLoading] = useState(false);

  const handleRefresh = useCallback(() => {
    setRefreshNonce((prev) => prev + 1);
    if (activeTab === 'settings') {
      refetchSettings();
    } else if (activeTab === 'heatmaps') {
      refetchMetrics();
      refetchSettings();
    } else if (activeTab === 'funnels' || activeTab === 'mining') {
      refetchCustomer();
    } else if (activeTab === 'replay') {
      setReplayLoading(true);
    } else if (activeTab === 'friction') {
      setFrictionLoading(true);
    }
  }, [activeTab, refetchSettings, refetchMetrics, refetchCustomer]);

  const visibleTabs = TABS.filter((tab) => {
    if (tab.id === 'replay' && !hasSessionReplayFeature) {
      return false;
    }
    if (isHeatmapSection && !tab.requiresHeatmap) return false;
    if (isCustomerAnalyticsSection && !tab.requiresCustomerAnalytics) return false;
    if (tab.requiresHeatmap && !hasHeatmapFeature) return false;
    if (tab.requiresCustomerAnalytics && !hasCustomerAnalyticsFeature) return false;
    return true;
  });

  const getHeaderInfo = () => {
    if (isHeatmapSection) {
      return {
        title: 'Heatmaps',
        subtitle: 'Analyze user interactions, click density grids, and heatmap capture settings.'
      };
    }
    if (isCustomerAnalyticsSection) {
      return {
        title: 'Behavioural Analytics',
        subtitle: 'Analyze user behavioral funnels, multi-dimensional audience breakdowns, and journey paths.'
      };
    }
    return {
      title: 'Behavioural Analytics & Heatmaps',
      subtitle: 'Analyze user interactions, click density grids, behavioral funnels, and automated ML user flow discoveries.'
    };
  };

  const { title: hubTitle, subtitle: hubSubtitle } = getHeaderInfo();

  const isEntitled = hasHeatmapFeature || hasCustomerAnalyticsFeature;

  if (!isEntitled) {
    return (
      <div className="enduser-hub-container">
        <div className="enduser-hub-denied">
          <FaShieldAlt className="denied-icon" />
          <h3>Heatmap & Customer Analytics Feature Required</h3>
          <p>
            Your organization does not have an active subscription tier for Heatmaps or Customer Analytics.
            Please upgrade your plan or contact your administrator to enable these capabilities.
          </p>
        </div>
      </div>
    );
  }

  const isGeneralLoading = activeTab === 'settings'
    ? settingsLoading
    : activeTab === 'heatmaps'
      ? (metricsLoading || settingsLoading)
      : (activeTab === 'funnels' || activeTab === 'mining')
        ? customerLoading
        : activeTab === 'replay'
          ? replayLoading
          : activeTab === 'friction'
            ? frictionLoading
            : false;

  return (
    <div className="enduser-hub-container" data-testid="enduser-analytics-hub">
      <div className="enduser-hub-header">
        <div className="enduser-hub-title">
          <h2>{hubTitle}</h2>
          <p className="enduser-hub-subtitle">
            {hubSubtitle}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="enduser-hub-nav" role="tablist">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`enduser-hub-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`hub-tab-${tab.id}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Shared Filter Toolbar */}
      <HeatmapFilterBar
        orgId={orgId}
        roles={roles}
        pages={settings?.pages || []} 
        selectedPageId={selectedPageId}
        selectedLayoutVersion={selectedLayoutVersion}
        onPageChange={handlePageChange}
        onLayoutVersionChange={setSelectedLayoutVersion}
        showPageSelector={activeTab === 'heatmaps' || activeTab === 'friction'}
        allowAllPages={activeTab === 'friction'}
        showDateFilter={activeTab !== 'settings'}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        isLoading={isGeneralLoading}
      />

      {/* Main Tab Content */}
      <div className="enduser-hub-content" role="tabpanel">
        {activeTab === 'settings' && (
          <HeatmapSettingsTab
            settings={settings}
            etag={etag}
            isLoading={settingsLoading}
            error={settingsError}
            isConcurrencyConflict={isConcurrencyConflict}
            onSave={saveSettings}
            onReload={refetchSettings}
            onManageDomains={onNavigate ? () => onNavigate('sitecollections', { organizationId: filters.organizationId }) : undefined}
            organizationId={filters.organizationId}
            siteCollectionId={filters.siteCollectionId}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
          />
        )}

        {activeTab === 'heatmaps' && (
          <HeatmapViewerTab
            summary={summary}
            daily={daily}
            density={density}
            scrollReach={scrollReach}
            hoverDwell={hoverDwell}
            attention={attention}
            pages={settings?.pages || []}
            selectedPageId={selectedPageId}
            selectedLayoutVersion={selectedLayoutVersion}
            onPageChange={handlePageChange}
            onLayoutVersionChange={setSelectedLayoutVersion}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            organizationId={filters.organizationId}
            siteCollectionId={filters.siteCollectionId}
            hasSessionReplayFeature={hasSessionReplayFeature}
            isLoading={metricsLoading}
            error={metricsError}
            refreshNonce={refreshNonce}
          />
        )}

        {activeTab === 'funnels' && (
          <CustomerFunnelsTab
            report={report}
            funnel={funnel}
            comparison={comparison}
            checkpoints={checkpoints}
            actions={settings?.actions || []}
            funnelActionIds={funnelActionIds}
            onFunnelActionsChange={setFunnelActionIds}
            onReviewCheckpoints={fetchCheckpointReview}
            isCheckpointsLoading={checkpointsLoading}
            onExportInteractions={exportInteractions}
            isLoading={customerLoading}
            error={customerError}
          />
        )}

        {activeTab === 'mining' && (
          <JourneyMiningTab
            mining={mining}
            actions={settings?.actions || []}
            pages={settings?.pages || []}
            selectedPageId={selectedPageId}
            onGenerateFlow={generateFlow}
            generatingFlow={generatingFlow}
            flowGenerationResult={flowGenerationResult}
            isLoading={customerLoading}
            error={customerError}
          />
        )}

        {activeTab === 'friction' && (
          <FrictionSignalsTab
            organizationId={filters.organizationId}
            siteCollectionId={filters.siteCollectionId}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            pageId={selectedPageId}
            pages={settings?.pages || []}
            hasSessionReplayFeature={hasSessionReplayFeature}
            onWatchReplay={handleWatchReplay}
            refreshNonce={refreshNonce}
            onLoadingChange={setFrictionLoading}
          />
        )}

        {activeTab === 'replay' && (
          <SessionReplayTab
            organizationId={filters.organizationId}
            siteCollectionId={filters.siteCollectionId}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            initialSessionId={selectedReplaySessionId}
            hasSessionReplayFeature={hasSessionReplayFeature}
            refreshNonce={refreshNonce}
            onLoadingChange={setReplayLoading}
          />
        )}
      </div>
    </div>
  );
}

EndUserAnalyticsHub.propTypes = {
  orgId: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  activeSection: PropTypes.string,
  hasHeatmapFeature: PropTypes.bool,
  hasCustomerAnalyticsFeature: PropTypes.bool,
  hasSessionReplayFeature: PropTypes.bool,
  onNavigate: PropTypes.func
};
